import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import { BackToDetailsLink } from '@/features/deviceDetails/BackToDetailsLink'
import { fetchAlarmTopics } from '@/features/alarmTopics/api'
import {
  fetchSystemSubscriptions,
  resetSystemSubscriptions,
  saveSystemSubscriptions,
} from '@/features/systemAlarmSubscriptions/api'
import type { TopicState } from '@/features/systemAlarmSubscriptions/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/alarm_subscriptions.html ("Manage subscriptions"): a
 * topic checkbox grid grouped by severity, each annotated Inherited/
 * Custom, with per-topic and per-severity Reset-to-fleet-default, and a
 * single Save that commits only the topics the user actually touched
 * (matching save_alarm_subscriptions()'s "overrides not included are left
 * completely alone" contract exactly - pendingChanges only ever holds
 * topics the checkbox was actually clicked for).
 */
export default function SystemAlarmSubscriptionsManagePage() {
  // Read directly from the URL, not frozen into local state at mount -- see
  // SettingsAlarmsPage's matching comment for the real bug this fixes.
  const [searchParams, setSearchParams] = useSearchParams()
  const controllerId = searchParams.get('controller_id') ?? ''
  // The site the user actually navigated here FROM -- see SettingsAlarmsPage's
  // matching comment. "← Back to site details" always returns to this one,
  // never whichever site the dropdown currently shows.
  const [originalControllerId] = useState(() => searchParams.get('controller_id') ?? '')
  const queryClient = useQueryClient()

  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  const topicsQuery = useQuery({ queryKey: ['alarm-topics'], queryFn: fetchAlarmTopics })
  const subsQuery = useQuery({
    queryKey: ['system-subscriptions', controllerId],
    queryFn: () => fetchSystemSubscriptions(controllerId),
    enabled: Boolean(controllerId),
  })

  const [pendingChanges, setPendingChanges] = useState<Record<number, boolean>>({})
  const [message, setMessage] = useState<string | null>(null)

  const selectedController = controllersQuery.data?.find((c) => c.controller_id === controllerId)

  const groups = useMemo(() => {
    const map = new Map<string, { alarm_severity_id: number; topics: NonNullable<typeof topicsQuery.data>['alarm_topics'] }>()
    for (const topic of topicsQuery.data?.alarm_topics ?? []) {
      const key = topic.alarm_severity_name || 'Uncategorized'
      if (!map.has(key)) map.set(key, { alarm_severity_id: topic.alarm_severity_id, topics: [] })
      map.get(key)!.topics.push(topic)
    }
    return map
  }, [topicsQuery.data])

  function effectiveState(topicId: number): TopicState {
    if (topicId in pendingChanges) return { is_subscribed: pendingChanges[topicId], is_custom: true }
    return subsQuery.data?.topics[String(topicId)] ?? { is_subscribed: false, is_custom: false }
  }

  function toggle(topicId: number, checked: boolean) {
    setPendingChanges((prev) => ({ ...prev, [topicId]: checked }))
    setMessage(null)
  }

  const saveMutation = useMutation({
    mutationFn: () => saveSystemSubscriptions(controllerId, pendingChanges),
    onSuccess: (result) => {
      setMessage(`Saved ${result.topics_updated} change(s).`)
      setPendingChanges({})
      queryClient.invalidateQueries({ queryKey: ['system-subscriptions', controllerId] })
    },
  })

  const resetTopicMutation = useMutation({
    mutationFn: (topicId: number) => resetSystemSubscriptions(controllerId, { alarm_topic_ids: [topicId] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['system-subscriptions', controllerId] }),
  })

  const resetSeverityMutation = useMutation({
    mutationFn: (severityId: number) => resetSystemSubscriptions(controllerId, { alarm_severity_id: severityId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['system-subscriptions', controllerId] }),
  })

  return (
    <div className="space-y-4">
      <BackToDetailsLink controllerId={originalControllerId} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-text-primary">
          Manage Subscriptions{selectedController && <> — <span className="text-text-secondary">{selectedController.controller_name}</span></>}
        </h1>
        {controllerId && (
          <Link
            to={`/system-alarm-subscriptions?controller_id=${controllerId}`}
            className="rounded border border-border px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
          >
            ← Back to Overview
          </Link>
        )}
      </div>

      <div className="rounded border border-border bg-card p-4">
        <label className="mb-1 block text-xs text-text-secondary">Controller</label>
        <select
          value={controllerId}
          onChange={(e) => {
            setSearchParams(e.target.value ? { controller_id: e.target.value } : {})
            setPendingChanges({})
            setMessage(null)
          }}
          className="w-full max-w-sm rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
        >
          <option value="">Select a controller…</option>
          {(controllersQuery.data ?? []).map((c) => (
            <option key={c.controller_id} value={c.controller_id}>
              {c.controller_name || c.controller_id}
            </option>
          ))}
        </select>
      </div>

      {controllerId && subsQuery.isLoading && <LoadingState label="Loading subscriptions…" />}
      {controllerId && subsQuery.isError && (
        <ErrorState
          message={subsQuery.error instanceof Error ? subsQuery.error.message : 'Failed to load.'}
          onRetry={() => subsQuery.refetch()}
        />
      )}

      {controllerId && subsQuery.data && (
        <>
          {message && <p className="text-sm text-brand">{message}</p>}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {Array.from(groups.entries()).map(([severityName, group]) => (
              <div key={severityName} className="rounded border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                  <span className="text-sm font-semibold text-text-primary">{severityName}</span>
                  <button
                    type="button"
                    onClick={() => resetSeverityMutation.mutate(group.alarm_severity_id)}
                    disabled={resetSeverityMutation.isPending}
                    className="text-xs text-accent hover:underline disabled:opacity-50"
                  >
                    Reset all to fleet default
                  </button>
                </div>
                <div className="p-2">
                  {group.topics.map((topic) => {
                    const state = effectiveState(topic.alarm_topic_id)
                    return (
                      <div
                        key={topic.alarm_topic_id}
                        className="flex items-center justify-between border-b border-border py-1 text-sm last:border-0"
                      >
                        <label className="flex flex-1 items-center gap-2 text-text-primary">
                          <input
                            type="checkbox"
                            checked={state.is_subscribed}
                            onChange={(e) => toggle(topic.alarm_topic_id, e.target.checked)}
                          />
                          {topic.alarm_real_name}
                        </label>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs ${state.is_custom ? 'text-accent' : 'text-text-secondary'}`}
                            title={
                              state.is_custom
                                ? undefined
                                : state.source === 'site_severity'
                                  ? 'Following your severity subscription for this system'
                                  : 'Following your fleet default'
                            }
                          >
                            {state.is_custom ? 'Custom' : 'Inherited'}
                          </span>
                          {state.is_custom && (
                            <button
                              type="button"
                              onClick={() => resetTopicMutation.mutate(topic.alarm_topic_id)}
                              disabled={resetTopicMutation.isPending}
                              className="text-xs text-accent hover:underline disabled:opacity-50"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={Object.keys(pendingChanges).length === 0 || saveMutation.isPending}
            className="rounded bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : `Save (${Object.keys(pendingChanges).length} changed)`}
          </button>
        </>
      )}
    </div>
  )
}
